import { useEffect, useState } from "react";
import { Plus, PencilLine, Power, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { StatusBadge } from "../../components/shared/StatusBadge";
import { EmptyState } from "../../components/shared/EmptyState";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { api, formatApiError } from "../../lib/api";

const ROLES = [
  { id: "super_admin", label: "Administrateur principal" },
  { id: "collaborator", label: "Collaborateur" },
  { id: "logistics", label: "Planning & Logistique" },
];
const ROLE_LABELS = Object.fromEntries(ROLES.map((r) => [r.id, r.label]));
const LEVELS = [
  { id: "none", label: "Aucun" },
  { id: "read", label: "Lecture" },
  { id: "write", label: "Écriture" },
  { id: "full", label: "Complet" },
];
const EMPTY = { email: "", first_name: "", last_name: "", role: "collaborator", permissions: {}, password: "" };

export default function UtilisateursPage() {
  const [users, setUsers] = useState(null);
  const [resources, setResources] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => {
    api.get("/admin/users").then((r) => setUsers(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
    api.get("/admin/users/permission-resources").then((r) => setResources(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (u) => {
    setEditing(u);
    setForm({ email: u.email, first_name: u.first_name || "", last_name: u.last_name || "", role: u.role, permissions: u.permissions || {}, password: "" });
    setOpen(true);
  };

  const setPerm = (key, level) => setForm((f) => ({ ...f, permissions: { ...f.permissions, [key]: level } }));

  const submit = async () => {
    const payload = { ...form, password: form.password || null };
    try {
      if (editing) { await api.patch(`/admin/users/${editing.id}`, payload); toast.success("Compte mis à jour"); }
      else { await api.post("/admin/users", payload); toast.success("Compte créé — un e-mail d'accueil a été envoyé"); }
      setOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggle = async (u) => {
    if (!u.disabled && !window.confirm(`${u.disabled ? "Réactiver" : "Désactiver"} le compte de ${u.email} ?`)) return;
    try { await api.post(`/admin/users/${u.id}/toggle-disabled`); toast.success(u.disabled ? "Compte réactivé" : "Compte désactivé — accès immédiatement bloqué"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!users) return <div className="py-16 text-center font-heading text-deepgreen" data-testid="utilisateurs-loading">Chargement…</div>;

  return (
    <div data-testid="admin-utilisateurs-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="overline-ae">B22 — Utilisateurs &amp; droits</p>
          <h1 className="mt-1 font-heading text-2xl sm:text-3xl text-deepgreen">Utilisateurs &amp; droits</h1>
          <p className="mt-1 text-sm text-muted-foreground">Comptes d'administration et permissions granulaires. Les données financières (Paiements &amp; échéances) ne sont visibles qu'avec la permission dédiée.</p>
        </div>
        <button type="button" onClick={openCreate} className="btn-primary-ae !px-5 !py-2.5 !text-xs" data-testid="user-create">
          <Plus className="h-4 w-4" /> Nouveau compte
        </button>
      </div>

      {users.length === 0 ? (
        <div className="mt-6"><EmptyState title="Aucun compte">Créez le premier compte collaborateur.</EmptyState></div>
      ) : (
        <div className="card-ae mt-6 overflow-x-auto">
          <table className="w-full min-w-[720px] text-sm" data-testid="users-table">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="px-5 py-3">Nom</th>
                <th className="px-5 py-3">E-mail</th>
                <th className="px-5 py-3">Rôle</th>
                <th className="px-5 py-3">Permissions</th>
                <th className="px-5 py-3">Statut</th>
                <th className="px-5 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {users.map((u) => (
                <tr key={u.id} className="transition-colors hover:bg-cream/60" data-testid={`user-row-${u.id}`}>
                  <td className="px-5 py-3.5 font-semibold text-deepgreen">{u.first_name} {u.last_name}</td>
                  <td className="px-5 py-3.5 text-muted-foreground">{u.email}</td>
                  <td className="px-5 py-3.5">
                    <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-wider ${u.role === "super_admin" ? "bg-gold-light text-deepgreen" : "bg-sage-light text-deepgreen"}`}>
                      <ShieldCheck className="h-3 w-3" /> {ROLE_LABELS[u.role] || u.role}
                    </span>
                  </td>
                  <td className="px-5 py-3.5 text-xs text-muted-foreground">
                    {u.role === "super_admin" ? "Accès complet à tout" : `${Object.values(u.permissions || {}).filter((v) => v && v !== "none").length} module(s) autorisé(s)`}
                  </td>
                  <td className="px-5 py-3.5"><StatusBadge status={u.disabled ? "Archivé" : "Actif"} /></td>
                  <td className="px-5 py-3.5">
                    <div className="flex justify-end gap-2">
                      <button type="button" onClick={() => openEdit(u)} className="rounded-full border border-border px-3 py-1.5 text-[11px] font-bold text-deepgreen hover:bg-sage-light" data-testid={`user-edit-${u.id}`}><PencilLine className="h-3.5 w-3.5" /></button>
                      <button type="button" onClick={() => toggle(u)} className="rounded-full border border-border px-3 py-1.5 text-[11px] font-bold text-deepgreen hover:bg-sage-light" data-testid={`user-toggle-${u.id}`}>
                        <Power className="mr-1 inline h-3.5 w-3.5" />{u.disabled ? "Réactiver" : "Désactiver"}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto bg-cream sm:max-w-2xl" data-testid="user-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-deepgreen">{editing ? "Modifier le compte" : "Nouveau compte"}</DialogTitle>
            <DialogDescription>Le mot de passe n'est jamais envoyé par e-mail — communiquez-le séparément.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div><label className="label-ae" htmlFor="u-first">Prénom</label><input id="u-first" className="input-ae" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="user-first-name" /></div>
              <div><label className="label-ae" htmlFor="u-last">Nom</label><input id="u-last" className="input-ae" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="user-last-name" /></div>
              <div className="sm:col-span-2"><label className="label-ae" htmlFor="u-email">E-mail</label><input id="u-email" type="email" className="input-ae" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email" /></div>
              <div>
                <label className="label-ae" htmlFor="u-role">Rôle</label>
                <select id="u-role" className="input-ae" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="user-role">
                  {ROLES.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
                </select>
              </div>
              <div>
                <label className="label-ae" htmlFor="u-password">{editing ? "Nouveau mot de passe (vide = inchangé)" : "Mot de passe initial"}</label>
                <input id="u-password" type="password" className="input-ae" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="10 caractères minimum" data-testid="user-password" />
              </div>
            </div>

            {form.role === "super_admin" ? (
              <p className="rounded-lg bg-gold-light px-4 py-3 text-xs text-deepgreen" data-testid="user-superadmin-note">
                L'administrateur principal a accès à tout, y compris les paramètres techniques et les données financières.
              </p>
            ) : (
              <div data-testid="user-permissions-matrix">
                <p className="label-ae">Permissions par module</p>
                <div className="mt-2 space-y-2">
                  {resources.map((res) => (
                    <div key={res.key} className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-white px-4 py-2.5" data-testid={`perm-row-${res.key}`}>
                      <span className="text-xs font-semibold text-deepgreen">{res.label}</span>
                      <div className="flex gap-1">
                        {LEVELS.map((lvl) => (
                          <button key={lvl.id} type="button" onClick={() => setPerm(res.key, lvl.id)} data-testid={`perm-${res.key}-${lvl.id}`}
                            className={`rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-wider transition-colors ${(form.permissions[res.key] || "none") === lvl.id ? "bg-deepgreen text-cream" : "bg-cream text-muted-foreground hover:bg-sage-light"}`}>
                            {lvl.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                <p className="mt-2 text-[11px] text-muted-foreground">Sans permission « Paiements & échéances », les montants et données financières sont masqués.</p>
              </div>
            )}

            <button type="button" onClick={submit} disabled={form.email.length < 5 || !form.first_name || !form.last_name || (!editing && form.password.length < 10)} className="btn-primary-ae modal-sticky-btn w-full disabled:opacity-50" data-testid="user-submit">
              {editing ? "Enregistrer" : "Créer le compte"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
